/**
 * SkillDelivery Platform
 *
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout';
import adminApi from '../../services/adminApi';
import './PromptManagement.css';

// Session storage key for 2FA code
const TOTP_SESSION_KEY = 'prompt_2fa_code';

const CATEGORIES = [
  { value: '', label: 'All Categories' },
  { value: 'skillbot', label: 'SkillBot' },
  { value: 'dashboard', label: 'Dashboard' },
  { value: 'notification', label: 'Notification' },
  { value: 'suggestion', label: 'Suggestion' },
  { value: 'onboarding', label: 'Onboarding' },
  { value: 'insights', label: 'Insights' },
  { value: 'skillcode', label: 'SkillCode' }
];

const ENTITY_TYPES = [
  { value: '', label: 'All Entity Types' },
  { value: 'individual', label: 'Individual' },
  { value: 'organization', label: 'Organization' },
  { value: 'university', label: 'University' },
  { value: 'institute', label: 'Institute' }
];

const VERTICALS = [
  { value: '', label: 'All Verticals' },
  { value: 'wms', label: 'WMS' },
  { value: 'iseries', label: 'iSeries' },
  { value: 'civil', label: 'Civil' },
  { value: 'stitching', label: 'Stitching' },
  { value: 'erpnext', label: 'ERPNext' },
  { value: 'platform', label: 'Platform' },
  { value: 'skillcode', label: 'SkillCode' }
];

const SEAL_STATUS_CONFIG = {
  healthy: { label: 'Healthy', bg: '#ecfdf5', color: '#065f46', icon: '🟢' },
  candidate_available: { label: 'Candidate Available', bg: '#fffbeb', color: '#92400e', icon: '🟡' },
  seal_managed: { label: 'SEAL Managed', bg: '#eff6ff', color: '#1e40af', icon: '🔵' },
  monitoring: { label: 'Monitoring', bg: '#fff7ed', color: '#9a3412', icon: '🟠' }
};

const PromptManagement = () => {
  const navigate = useNavigate();
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [filters, setFilters] = useState({ category: '', entityType: '', vertical: '' });
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);

  // SEAL State
  const [sealFilter, setSealFilter] = useState(false);
  // Agent Platform filter — surfaces archetype.* + persona.* prompts seeded
  // by services/core-api/src/scripts/seed-agent-archetype-prompts.js +
  // seed-persona-prompts.js (tagged 'agent-platform' per ADR-018 §9).
  const [agentFilter, setAgentFilter] = useState(false);
  const [showVersionHistory, setShowVersionHistory] = useState(null);
  const [versionHistory, setVersionHistory] = useState([]);
  const [versionLoading, setVersionLoading] = useState(false);
  const [showDiff, setShowDiff] = useState(null);
  const [diffData, setDiffData] = useState(null);
  const [showSealColumns, setShowSealColumns] = useState(true);

  // 2FA State
  const [requires2FA, setRequires2FA] = useState(false);
  const [requires2FASetup, setRequires2FASetup] = useState(false);
  const [totpCode, setTotpCode] = useState('');
  const [verifying2FA, setVerifying2FA] = useState(false);
  const [is2FAVerified, setIs2FAVerified] = useState(false);

  // Get TOTP code from session or state
  const getStoredTotpCode = useCallback(() => {
    return sessionStorage.getItem(TOTP_SESSION_KEY) || '';
  }, []);

  // Store TOTP code in session
  const storeTotpCode = useCallback((code) => {
    sessionStorage.setItem(TOTP_SESSION_KEY, code);
    setIs2FAVerified(true);
  }, []);

  // Create headers with TOTP code
  const getAuthHeaders = useCallback(() => {
  // Use login 2FA session first (doesn't expire as quickly)
  const sessionToken = localStorage.getItem('admin2FASession');
  if (sessionToken) {
    return { 'x-2fa-session': sessionToken };
  }
  // Fallback to TOTP code
  const code = getStoredTotpCode();
  if (code) {
    return { 'x-totp-code': code };
  }
  return {};
}, [getStoredTotpCode]);

  // Check if we already have a valid 2FA session on mount
  useEffect(() => {
  const sessionToken = localStorage.getItem('admin2FASession');
  if (sessionToken) {
    setIs2FAVerified(true);
  } else {
    fetchPrompts();
  }
}, []);

  // Fetch prompts when filters change (only if verified)
  useEffect(() => {
    if (is2FAVerified) {
      fetchPrompts();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, is2FAVerified]);

  const fetchPrompts = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filters.category) params.category = filters.category;
      if (filters.entityType) params.entityType = filters.entityType;
      if (filters.vertical) params.vertical = filters.vertical;

      const response = await adminApi.get('/api/admin/prompt-templates', {
        params,
        headers: getAuthHeaders()
      });
      setPrompts(response.data?.templates || []);
      setError(null);
      setRequires2FA(false);
    } catch (err) {
      console.error('Fetch prompts error:', err);
      const errorData = err.response?.data;

      // Check if 2FA is required
      if (errorData?.code === '2FA_REQUIRED' || errorData?.requires2FA) {
        setRequires2FA(true);
        setIs2FAVerified(false);
        sessionStorage.removeItem(TOTP_SESSION_KEY);
      } else if (errorData?.code === '2FA_NOT_CONFIGURED' || errorData?.requires2FASetup) {
        setRequires2FASetup(true);
      } else if (errorData?.code === '2FA_INVALID') {
        setRequires2FA(true);
        setError('Invalid 2FA code. Please try again.');
        sessionStorage.removeItem(TOTP_SESSION_KEY);
        setIs2FAVerified(false);
      } else {
        setError(errorData?.error || 'Failed to fetch prompts');
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle 2FA verification
  const handleVerify2FA = async () => {
    if (!totpCode || totpCode.length !== 6) {
      setError('Please enter a valid 6-digit code');
      return;
    }

    setVerifying2FA(true);
    setError(null);

    try {
      // Store the code and try to fetch
      storeTotpCode(totpCode);
      setRequires2FA(false);
      setTotpCode('');
      await fetchPrompts();
    } catch (err) {
      setError('Verification failed. Please try again.');
      sessionStorage.removeItem(TOTP_SESSION_KEY);
      setIs2FAVerified(false);
      setRequires2FA(true);
    } finally {
      setVerifying2FA(false);
    }
  };

  const handleSave = async (promptData) => {
    try {
      setSaving(true);
      if (promptData._id) {
        // Update existing
        await adminApi.put(`/api/admin/prompt-templates/${promptData.promptKey}`, {
          template: promptData.template,
          systemPrompt: promptData.systemPrompt,
          variables: promptData.variables,
          reason: 'Admin UI update'
        }, { headers: getAuthHeaders() });
      } else {
        // Create new
        await adminApi.post('/api/admin/prompt-templates', promptData, { headers: getAuthHeaders() });
      }
      setIsEditing(false);
      setSelectedPrompt(null);
      fetchPrompts();
    } catch (err) {
      const errorData = err.response?.data;
      if (errorData?.code === '2FA_REQUIRED' || errorData?.code === '2FA_INVALID') {
        setRequires2FA(true);
        sessionStorage.removeItem(TOTP_SESSION_KEY);
        setIs2FAVerified(false);
      } else {
        alert('Error saving prompt: ' + (errorData?.error || err.message));
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (promptKey) => {
    if (!window.confirm('Are you sure you want to deactivate this prompt?')) return;

    try {
      await adminApi.delete(`/api/admin/prompt-templates/${promptKey}`, { headers: getAuthHeaders() });
      fetchPrompts();
    } catch (err) {
      const errorData = err.response?.data;
      if (errorData?.code === '2FA_REQUIRED' || errorData?.code === '2FA_INVALID') {
        setRequires2FA(true);
        sessionStorage.removeItem(TOTP_SESSION_KEY);
        setIs2FAVerified(false);
      } else {
        alert('Error deleting prompt: ' + (errorData?.error || err.message));
      }
    }
  };

  const handleSeedDefaults = async () => {
    if (!window.confirm('Seed default prompt templates? This will add missing templates.')) return;

    try {
      setSeeding(true);
      await adminApi.post('/api/admin/prompt-templates/seed', {}, { headers: getAuthHeaders() });
      alert('Default templates seeded successfully!');
      fetchPrompts();
    } catch (err) {
      const errorData = err.response?.data;
      if (errorData?.code === '2FA_REQUIRED' || errorData?.code === '2FA_INVALID') {
        setRequires2FA(true);
        sessionStorage.removeItem(TOTP_SESSION_KEY);
        setIs2FAVerified(false);
      } else {
        alert('Error seeding templates: ' + (errorData?.error || err.message));
      }
    } finally {
      setSeeding(false);
    }
  };

  // ═══════════════ SEAL Methods ═══════════════
  const fetchVersionHistory = async (promptKey) => {
    setVersionLoading(true);
    try {
      const res = await adminApi.get(`/api/seal/prompts/${encodeURIComponent(promptKey)}/versions`);
      if (res.data?.success) {
        setVersionHistory(res.data.data.versions || []);
        setShowVersionHistory(promptKey);
      }
    } catch (err) {
      console.error('Version history error:', err);
      setVersionHistory([]);
      setShowVersionHistory(promptKey);
    } finally {
      setVersionLoading(false);
    }
  };

  const fetchDiff = async (promptKey, vA, vB) => {
    try {
      const res = await adminApi.get(`/api/seal/prompts/${encodeURIComponent(promptKey)}/diff/${vA}/${vB}`);
      if (res.data?.success) {
        setDiffData(res.data.data);
        setShowDiff({ key: promptKey, versionA: vA, versionB: vB });
      }
    } catch (err) {
      console.error('Diff error:', err);
    }
  };

  const handleSealStatusUpdate = async (promptKey, newStatus) => {
    try {
      await adminApi.put(`/api/seal/prompts/${encodeURIComponent(promptKey)}/seal-status`, {
        seal_status: newStatus
      });
      fetchPrompts();
    } catch (err) {
      console.error('SEAL status update error:', err);
    }
  };

  const handleForceRefresh = async (promptKey) => {
    try {
      await adminApi.post(`/api/seal/prompts/${encodeURIComponent(promptKey)}/force-refresh`);
      alert('Prompt cache refreshed');
    } catch (err) {
      console.error('Force refresh error:', err);
    }
  };

  // Apply SEAL + Agent Platform filters. Both AND together when active.
  const filteredPrompts = prompts.filter(p => {
    if (sealFilter
        && !(p.managed_by === 'seal' || (p.seal_status && p.seal_status !== 'healthy'))) {
      return false;
    }
    if (agentFilter && !(Array.isArray(p.tags) && p.tags.includes('agent-platform'))) {
      return false;
    }
    return true;
  });

  // Show 2FA Setup Required screen
  if (requires2FASetup) {
    return (
      <AdminLayout showBackButton backPath="/admin/dashboard">
        <div className="prompt-management">
          <div className="twofa-setup-required">
            <div className="twofa-setup-card">
              <div className="twofa-setup-icon">🔐</div>
              <h2>2FA Required for Prompt Management</h2>
              <p>
                Two-factor authentication must be enabled to access prompt management.
                This is a security requirement to protect sensitive system prompts.
              </p>
              <p className="twofa-setup-instruction">
                Please go to your admin profile settings and enable 2FA using an authenticator app.
              </p>
              <button
                className="btn btn-primary"
                onClick={() => navigate('/admin/settings')}
              >
                Go to Settings
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => navigate('/admin/dashboard')}
              >
                Back to Dashboard
              </button>
            </div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  // Show 2FA Verification modal
  if (requires2FA) {
    return (
      <AdminLayout showBackButton backPath="/admin/dashboard">
        <div className="prompt-management">
          <div className="twofa-modal-overlay">
            <div className="twofa-modal">
              <div className="twofa-modal-header">
                <span className="twofa-icon">🔐</span>
                <h2>2FA Verification Required</h2>
              </div>
              <p>Enter the 6-digit code from your authenticator app to access prompt management.</p>

              {error && (
                <div className="twofa-error">
                  <span>⚠️</span> {error}
                </div>
              )}

              <div className="twofa-input-group">
                <input
                  type="text"
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  maxLength={6}
                  className="twofa-input"
                  autoFocus
                  onKeyDown={(e) => e.key === 'Enter' && handleVerify2FA()}
                />
              </div>

              <div className="twofa-actions">
                <button
                  className="btn btn-secondary"
                  onClick={() => navigate('/admin/dashboard')}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleVerify2FA}
                  disabled={verifying2FA || totpCode.length !== 6}
                >
                  {verifying2FA ? '🔄 Verifying...' : '✓ Verify'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout showBackButton backPath="/admin/dashboard">
      <div className="prompt-management">
        {/* Header */}
        <div className="page-header">
          <div className="header-left">
            <h1>
              <span className="header-icon">📝</span>
              Prompt Management
            </h1>
            <p className="header-subtitle">
              Manage dynamic prompts for SkillBot, Dashboard, and Notifications
            </p>
          </div>
          <div className="header-actions">
            <button
              className="btn btn-secondary"
              onClick={handleSeedDefaults}
              disabled={seeding}
            >
              {seeding ? '🔄 Seeding...' : '🌱 Seed Defaults'}
            </button>
            <button
              className="btn btn-primary"
              onClick={() => { setSelectedPrompt({}); setIsEditing(true); }}
            >
              ➕ New Prompt
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="filters-section">
          <select
            value={filters.category}
            onChange={(e) => setFilters({ ...filters, category: e.target.value })}
            className="filter-select"
          >
            {CATEGORIES.map(cat => (
              <option key={cat.value} value={cat.value}>{cat.label}</option>
            ))}
          </select>
          <select
            value={filters.entityType}
            onChange={(e) => setFilters({ ...filters, entityType: e.target.value })}
            className="filter-select"
          >
            {ENTITY_TYPES.map(et => (
              <option key={et.value} value={et.value}>{et.label}</option>
            ))}
          </select>
          <select
            value={filters.vertical}
            onChange={(e) => setFilters({ ...filters, vertical: e.target.value })}
            className="filter-select"
          >
            {VERTICALS.map(v => (
              <option key={v.value} value={v.value}>{v.label}</option>
            ))}
          </select>
          <button
            className={`filter-select ${sealFilter ? 'seal-filter-active' : ''}`}
            onClick={() => setSealFilter(!sealFilter)}
            style={{
              background: sealFilter ? '#eff6ff' : '#fff',
              color: sealFilter ? '#1e40af' : '#475569',
              border: `1px solid ${sealFilter ? '#93c5fd' : '#e2e8f0'}`,
              cursor: 'pointer',
              fontWeight: sealFilter ? 600 : 400
            }}
          >
            🤖 {sealFilter ? 'SEAL Managed ✓' : 'SEAL Managed'}
          </button>
          <button
            className={`filter-select ${agentFilter ? 'agent-filter-active' : ''}`}
            onClick={() => setAgentFilter(!agentFilter)}
            title="Show archetype.* and persona.* prompts used by the Workflow Factory (ADR-018)"
            style={{
              background: agentFilter ? '#ecfeff' : '#fff',
              color: agentFilter ? '#0e7490' : '#475569',
              border: `1px solid ${agentFilter ? '#67e8f9' : '#e2e8f0'}`,
              cursor: 'pointer',
              fontWeight: agentFilter ? 600 : 400
            }}
          >
            🧩 {agentFilter ? 'Agent Platform ✓' : 'Agent Platform'}
          </button>
          <button
            className="filter-select"
            onClick={() => setShowSealColumns(!showSealColumns)}
            style={{ cursor: 'pointer', background: '#fff', border: '1px solid #e2e8f0', color: '#475569' }}
            title="Toggle SEAL columns"
          >
            {showSealColumns ? '◉ SEAL Cols' : '○ SEAL Cols'}
          </button>
        </div>

        {/* Error */}
        {error && (
          <div className="error-banner">
            <span className="error-icon">⚠️</span>
            <span>{error}</span>
            <button onClick={() => setError(null)}>×</button>
          </div>
        )}

        {/* Stats */}
        <div className="stats-bar">
          <div className="stat-item">
            <span className="stat-value">{filteredPrompts.length}</span>
            <span className="stat-label">Templates</span>
          </div>
          <div className="stat-item">
            <span className="stat-value">{prompts.filter(p => p.category === 'skillbot').length}</span>
            <span className="stat-label">SkillBot</span>
          </div>
          <div className="stat-item">
            <span className="stat-value">{prompts.filter(p => p.category === 'dashboard').length}</span>
            <span className="stat-label">Dashboard</span>
          </div>
          <div className="stat-item">
            <span className="stat-value">{prompts.filter(p => p.seal_status && p.seal_status !== 'healthy').length}</span>
            <span className="stat-label">SEAL Active</span>
          </div>
        </div>

        {/* Prompts Table */}
        <div className="prompts-table-container">
          {loading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading prompts...</p>
            </div>
          ) : filteredPrompts.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">📝</div>
              <p>{sealFilter ? 'No SEAL-managed prompts found' : 'No prompts found'}</p>
              {!sealFilter && (
                <button className="btn btn-primary" onClick={handleSeedDefaults}>
                  🌱 Seed Default Templates
                </button>
              )}
            </div>
          ) : (
            <table className="prompts-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Key</th>
                  <th>Category</th>
                  <th>Entity Type</th>
                  <th>Vertical</th>
                  <th>Version</th>
                  {showSealColumns && <th>SEAL Status</th>}
                  {showSealColumns && <th>Eval Score</th>}
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredPrompts.map((prompt) => {
                  const sealCfg = SEAL_STATUS_CONFIG[prompt.seal_status] || SEAL_STATUS_CONFIG.healthy;
                  const evalScore = prompt.eval_scores?.effectiveness;
                  return (
                    <React.Fragment key={prompt._id}>
                      <tr>
                        <td className="prompt-name">{prompt.name}</td>
                        <td className="prompt-key">
                          <code>{prompt.promptKey}</code>
                        </td>
                        <td>
                          <span className={`badge badge-${prompt.category}`}>
                            {prompt.category}
                          </span>
                        </td>
                        <td>
                          {prompt.entityType ? (
                            <span className="entity-badge">{prompt.entityType}</span>
                          ) : (
                            <span className="text-muted">All</span>
                          )}
                        </td>
                        <td>
                          {prompt.vertical ? (
                            <span className="vertical-badge">{prompt.vertical}</span>
                          ) : (
                            <span className="text-muted">All</span>
                          )}
                        </td>
                        <td className="version-cell">
                          <button
                            onClick={() => fetchVersionHistory(prompt.promptKey)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'monospace', color: '#4f46e5', textDecoration: 'underline' }}
                            title="View version history"
                          >
                            v{prompt.version}
                          </button>
                        </td>
                        {showSealColumns && (
                          <td>
                            <span style={{
                              display: 'inline-block', padding: '3px 8px', borderRadius: '10px', fontSize: '0.75rem',
                              fontWeight: 500, background: sealCfg.bg, color: sealCfg.color
                            }}>
                              {sealCfg.icon} {sealCfg.label}
                            </span>
                          </td>
                        )}
                        {showSealColumns && (
                          <td style={{ fontFamily: 'monospace', color: evalScore != null ? '#111' : '#94a3b8' }}>
                            {evalScore != null ? evalScore.toFixed(2) : '—'}
                          </td>
                        )}
                        <td className="actions-cell">
                          <button
                            className="action-btn edit"
                            onClick={() => { setSelectedPrompt(prompt); setIsEditing(true); }}
                            title="Edit"
                          >
                            ✏️
                          </button>
                          <button
                            className="action-btn delete"
                            onClick={() => handleDelete(prompt.promptKey)}
                            title="Deactivate"
                          >
                            🗑️
                          </button>
                          <button
                            className="action-btn edit"
                            onClick={() => handleForceRefresh(prompt.promptKey)}
                            title="Force refresh cache"
                            style={{ background: '#e0e7ff' }}
                          >
                            🔄
                          </button>
                        </td>
                      </tr>
                      {/* SEAL Candidate Banner */}
                      {prompt.seal_status === 'candidate_available' && (
                        <tr>
                          <td colSpan={showSealColumns ? 9 : 7} style={{ padding: '8px 16px', background: '#fffbeb', borderBottom: '1px solid #fde68a' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '13px' }}>
                              <span>🤖 SEAL recommends a new version</span>
                              <button
                                onClick={() => fetchVersionHistory(prompt.promptKey)}
                                style={{ background: '#e0e7ff', border: 'none', padding: '4px 10px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', color: '#4338ca' }}
                              >
                                View Changes
                              </button>
                              <button
                                onClick={() => handleSealStatusUpdate(prompt.promptKey, 'seal_managed')}
                                style={{ background: '#d1fae5', border: 'none', padding: '4px 10px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', color: '#065f46' }}
                              >
                                Approve
                              </button>
                              <button
                                onClick={() => handleSealStatusUpdate(prompt.promptKey, 'healthy')}
                                style={{ background: '#fee2e2', border: 'none', padding: '4px 10px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', color: '#991b1b' }}
                              >
                                Dismiss
                              </button>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Edit Modal */}
        {isEditing && (
          <PromptEditModal
            prompt={selectedPrompt}
            onSave={handleSave}
            onClose={() => { setIsEditing(false); setSelectedPrompt(null); }}
            saving={saving}
          />
        )}

        {/* ═══════════ SEAL: Version History Modal ═══════════ */}
        {showVersionHistory && (
          <div className="modal-overlay" onClick={() => { setShowVersionHistory(null); setVersionHistory([]); }}>
            <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Version History — {showVersionHistory}</h2>
                <button className="modal-close" onClick={() => { setShowVersionHistory(null); setVersionHistory([]); }}>×</button>
              </div>
              <div className="modal-body">
                {versionLoading ? (
                  <div className="loading-state"><div className="spinner"></div><p>Loading versions...</p></div>
                ) : versionHistory.length === 0 ? (
                  <div className="empty-state"><p>No version history available</p></div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {versionHistory.map((v, idx) => (
                      <div key={idx} style={{
                        padding: '12px 16px', border: '1px solid #e5e7eb', borderRadius: '8px',
                        background: idx === 0 ? '#f0fdf4' : '#fff'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div>
                            <strong style={{ fontSize: '14px' }}>v{v.version}</strong>
                            <span style={{ marginLeft: '8px', fontSize: '12px', color: '#6b7280' }}>
                              {v.author === 'seal' ? '🤖 SEAL' : '✋ Manual'}
                            </span>
                            {idx === 0 && <span style={{ marginLeft: '8px', fontSize: '11px', color: '#059669', fontWeight: 600 }}>ACTIVE</span>}
                          </div>
                          <span style={{ fontSize: '12px', color: '#9ca3af' }}>
                            {v.created_at ? new Date(v.created_at).toLocaleDateString() : ''}
                          </span>
                        </div>
                        {v.change_summary && <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>{v.change_summary}</div>}
                        {idx > 0 && versionHistory[0]?.version && (
                          <button
                            onClick={() => fetchDiff(showVersionHistory, versionHistory[0].version, v.version)}
                            style={{ marginTop: '8px', background: '#e0e7ff', border: 'none', padding: '4px 10px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', color: '#4338ca' }}
                          >
                            Compare with Active
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => { setShowVersionHistory(null); setVersionHistory([]); }}>Close</button>
              </div>
            </div>
          </div>
        )}

        {/* ═══════════ SEAL: Diff View Modal ═══════════ */}
        {showDiff && diffData && (
          <div className="modal-overlay" onClick={() => { setShowDiff(null); setDiffData(null); }}>
            <div className="modal-content large" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px' }}>
              <div className="modal-header">
                <h2>Diff: v{showDiff.versionA} vs v{showDiff.versionB}</h2>
                <button className="modal-close" onClick={() => { setShowDiff(null); setDiffData(null); }}>×</button>
              </div>
              <div className="modal-body">
                <div style={{ marginBottom: '12px', fontSize: '13px', color: '#6b7280' }}>
                  <span style={{ color: '#16a34a', fontWeight: 600 }}>+{diffData.summary?.added || 0} added</span>
                  {' | '}
                  <span style={{ color: '#dc2626', fontWeight: 600 }}>-{diffData.summary?.removed || 0} removed</span>
                  {' | '}
                  <span>{diffData.summary?.unchanged || 0} unchanged</span>
                </div>
                <div style={{ fontFamily: 'monospace', fontSize: '12px', background: '#f8fafc', borderRadius: '8px', padding: '12px', maxHeight: '400px', overflowY: 'auto' }}>
                  {(diffData.changes || []).map((change, i) => (
                    <div key={i} style={{
                      padding: '2px 8px',
                      background: change.type === 'added' ? '#dcfce7' : change.type === 'removed' ? '#fee2e2' : 'transparent',
                      color: change.type === 'added' ? '#166534' : change.type === 'removed' ? '#991b1b' : '#374151',
                      borderLeft: change.type === 'added' ? '3px solid #16a34a' : change.type === 'removed' ? '3px solid #dc2626' : '3px solid transparent'
                    }}>
                      <span style={{ color: '#9ca3af', width: '30px', display: 'inline-block' }}>{change.lineNumber}</span>
                      {change.type === 'added' ? '+ ' : change.type === 'removed' ? '- ' : '  '}{change.line}
                    </div>
                  ))}
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => { setShowDiff(null); setDiffData(null); }}>Close</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

// Edit Modal Component
const PromptEditModal = ({ prompt, onSave, onClose, saving }) => {
  const [form, setForm] = useState({
    promptKey: '',
    name: '',
    description: '',
    category: 'skillbot',
    subcategory: '',
    entityType: null,
    vertical: null,
    template: '',
    systemPrompt: '',
    variables: [],
    ...prompt
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(form);
  };

  const addVariable = () => {
    setForm(prev => ({
      ...prev,
      variables: [...(prev.variables || []), { name: '', description: '', required: false }]
    }));
  };

  const updateVariable = (index, field, value) => {
    setForm(prev => {
      const vars = [...(prev.variables || [])];
      vars[index] = { ...vars[index], [field]: value };
      return { ...prev, variables: vars };
    });
  };

  const removeVariable = (index) => {
    setForm(prev => ({
      ...prev,
      variables: prev.variables.filter((_, i) => i !== index)
    }));
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="modal-header">
          <h2>{prompt._id ? 'Edit Prompt Template' : 'Create New Prompt Template'}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        {/* Modal Body */}
        <form onSubmit={handleSubmit} className="modal-body">
          {/* Basic Info Row */}
          <div className="form-row">
            <div className="form-group">
              <label>Prompt Key *</label>
              <input
                type="text"
                value={form.promptKey}
                onChange={(e) => setForm({ ...form, promptKey: e.target.value })}
                placeholder="e.g., skillbot.individual.job_search"
                required
                disabled={!!prompt._id}
              />
              <small>Unique identifier. Use dot notation for organization.</small>
            </div>
            <div className="form-group">
              <label>Display Name *</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g., SkillBot - Job Search Focus"
                required
              />
            </div>
          </div>

          {/* Category & Targeting Row */}
          <div className="form-row three-col">
            <div className="form-group">
              <label>Category *</label>
              <select
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
              >
                <option value="skillbot">SkillBot</option>
                <option value="dashboard">Dashboard</option>
                <option value="notification">Notification</option>
                <option value="suggestion">Suggestion</option>
                <option value="onboarding">Onboarding</option>
                <option value="insights">Insights</option>
                <option value="skillcode">SkillCode</option>
              </select>
            </div>
            <div className="form-group">
              <label>Entity Type</label>
              <select
                value={form.entityType || ''}
                onChange={(e) => setForm({ ...form, entityType: e.target.value || null })}
              >
                <option value="">All Entity Types</option>
                <option value="individual">Individual</option>
                <option value="organization">Organization</option>
                <option value="university">University</option>
                <option value="institute">Institute</option>
              </select>
            </div>
            <div className="form-group">
              <label>Vertical</label>
              <select
                value={form.vertical || ''}
                onChange={(e) => setForm({ ...form, vertical: e.target.value || null })}
              >
                <option value="">All Verticals</option>
                <option value="wms">WMS</option>
                <option value="iseries">iSeries</option>
                <option value="civil">Civil</option>
                <option value="stitching">Stitching</option>
                <option value="erpnext">ERPNext</option>
                <option value="platform">Platform</option>
                <option value="skillcode">SkillCode</option>
              </select>
            </div>
          </div>

          {/* Description */}
          <div className="form-group">
            <label>Description</label>
            <input
              type="text"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Brief description of what this prompt does"
            />
          </div>

          {/* System Prompt */}
          <div className="form-group">
            <label>
              System Prompt
              <span className="label-hint">(supports {"{{variables}}"} )</span>
            </label>
            <textarea
              value={form.systemPrompt || ''}
              onChange={(e) => setForm({ ...form, systemPrompt: e.target.value })}
              rows={4}
              placeholder="Enter the system prompt (context for the AI)..."
            />
          </div>

          {/* Template */}
          <div className="form-group">
            <label>
              Template *
              <span className="label-hint">(supports {"{{variables}}"} )</span>
            </label>
            <textarea
              value={form.template}
              onChange={(e) => setForm({ ...form, template: e.target.value })}
              rows={6}
              placeholder="Enter the main prompt template..."
              required
            />
          </div>

          {/* Variables */}
          <div className="form-group">
            <label>
              Variables
              <button type="button" className="btn-add-variable" onClick={addVariable}>
                + Add Variable
              </button>
            </label>
            <div className="variables-list">
              {(form.variables || []).map((variable, index) => (
                <div key={index} className="variable-row">
                  <input
                    type="text"
                    value={variable.name}
                    onChange={(e) => updateVariable(index, 'name', e.target.value)}
                    placeholder="Variable name (e.g., user.name)"
                  />
                  <input
                    type="text"
                    value={variable.description}
                    onChange={(e) => updateVariable(index, 'description', e.target.value)}
                    placeholder="Description"
                  />
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={variable.required}
                      onChange={(e) => updateVariable(index, 'required', e.target.checked)}
                    />
                    Required
                  </label>
                  <button
                    type="button"
                    className="btn-remove-variable"
                    onClick={() => removeVariable(index)}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>
        </form>

        {/* Modal Footer */}
        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={saving}
          >
            {saving ? '💾 Saving...' : '💾 Save Prompt'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PromptManagement;
